import {
  DOMAINS,
  type AttemptResult,
  type ChildProfile,
  type Domain,
  type DomainMastery,
  type ParentReport,
  type ProgressRepository
} from "@/domain";

const RECENT_ATTEMPT_LIMIT = 8;

export class ParentReportUnavailableError extends Error {
  override readonly name = "ParentReportUnavailableError";

  constructor() {
    super("The requested parent report is unavailable.");
  }
}

export function createParentReport(
  profile: ChildProfile,
  attempts: AttemptResult[],
  mastery: DomainMastery[]
): ParentReport {
  const domainOrder = new Map(
    DOMAINS.map((domain, index) => [domain, index])
  );
  const sortedMastery = [...mastery].sort(
    (left, right) =>
      getDomainOrder(domainOrder, left.domain) -
      getDomainOrder(domainOrder, right.domain)
  );
  const recentAttempts = [...attempts]
    .sort(
      (left, right) =>
        right.completedAt.localeCompare(left.completedAt) ||
        right.id.localeCompare(left.id)
    )
    .slice(0, RECENT_ATTEMPT_LIMIT);
  const totalDurationMs = attempts.reduce(
    (total, attempt) => total + attempt.durationMs,
    0
  );
  const scores = new Map(
    mastery.map((entry) => [entry.domain, entry.score])
  );
  const suggestedDomains = [...DOMAINS]
    .sort(
      (left, right) =>
        (scores.get(left) ?? 0) - (scores.get(right) ?? 0) ||
        getDomainOrder(domainOrder, left) - getDomainOrder(domainOrder, right)
    )
    .slice(0, 2);

  return {
    profile,
    mastery: sortedMastery,
    recentAttempts,
    practicedMinutes: Math.round(totalDurationMs / 6_000) / 10,
    suggestedDomains
  };
}

export async function loadParentReport(
  repository: ProgressRepository,
  profileId: string
): Promise<ParentReport> {
  const [profile, attempts, mastery] = await Promise.all([
    repository.getProfile(profileId),
    repository.listAttempts(profileId),
    repository.getMastery(profileId)
  ]);

  if (profile === undefined) {
    throw new ParentReportUnavailableError();
  }

  return createParentReport(profile, attempts, mastery);
}

function getDomainOrder(
  domainOrder: ReadonlyMap<Domain, number>,
  domain: Domain
): number {
  return domainOrder.get(domain) ?? DOMAINS.length;
}
